<template>
  <div class="msb">
    <div v-if="statusText" class="msb-status-row">
      <span class="msb-status" :class="{ warn: statusWarn }">{{ statusText }}</span>
    </div>

    <!-- KPI tiles: click to plot / unplot on the chart -->
    <div class="msb-kpis" :style="{ '--kpi-cols': metrics.length }">
      <button
        v-for="m in metrics"
        :key="m.key"
        type="button"
        :class="['msb-kpi', { active: selected.includes(m.key) }]"
        :style="{ '--kpi-color': m.color }"
        @click="toggle(m.key)"
      >
        <span class="msb-kpi-top"></span>
        <span class="msb-kpi-label">{{ m.label }}</span>
        <span class="msb-kpi-value">{{ kpiValue(m.key) }}</span>
        <span class="msb-kpi-plot">{{ selected.includes(m.key) ? 'En gráfica' : 'Mostrar' }}</span>
      </button>
    </div>

    <div class="msb-chart-wrap">
      <v-chart v-if="hasSeries && selected.length" :option="chartOption" autoresize style="height: 300px" />
      <div v-else class="msb-empty">
        <template v-if="loading">Cargando datos…</template>
        <template v-else-if="data && data.configured === false">{{ data.error || 'No disponible.' }}</template>
        <template v-else-if="data && data.error">{{ data.error }}</template>
        <template v-else-if="hasSeries && !selected.length">Selecciona una métrica para graficarla.</template>
        <template v-else>Sin datos en el periodo seleccionado.</template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  // [{ key, label, color, axis: 'count'|'money', fmt: 'int'|'num1'|'money'|'money2' }]
  metrics: { type: Array, required: true },
  data: { type: Object, default: null },        // { configured?, error?, totals, series }
  loading: { type: Boolean, default: false },
  defaultSelected: { type: Array, default: () => [] },
})

const selected = ref([...props.defaultSelected])

function toggle(key) {
  const i = selected.value.indexOf(key)
  if (i === -1) selected.value = [...selected.value, key]
  else selected.value = selected.value.filter(k => k !== key)
}

function fmtVal(key, v) {
  const n = Number(v || 0)
  const m = props.metrics.find(x => x.key === key)
  if (!m) return String(n)
  if (m.fmt === 'int')    return n.toLocaleString('es-MX', { maximumFractionDigits: 0 })
  if (m.fmt === 'num1')   return n.toLocaleString('es-MX', { maximumFractionDigits: 1 })
  if (m.fmt === 'money')  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 })
  if (m.fmt === 'money2') return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return String(n)
}

const notConfigured = computed(() => !!(props.data && props.data.configured === false))

function kpiValue(key) {
  if (!props.data || notConfigured.value) return '—'
  return fmtVal(key, props.data.totals ? props.data.totals[key] : 0)
}

const statusWarn = computed(() => notConfigured.value || !!(props.data && props.data.error))
const statusText = computed(() => {
  if (props.loading) return 'Cargando…'
  if (notConfigured.value) return 'No configurado'
  if (props.data && props.data.error) return 'Error'
  return ''
})

const hasSeries = computed(() => !!(props.data && props.data.configured !== false && (props.data.series || []).length))

function fmtAxisDate(iso, full) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  if (!m) return iso || ''
  return full ? `${m[3]}/${m[2]}/${m[1]}` : `${m[3]}/${m[2]}`
}

const chartOption = computed(() => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Monterrey' })
  const series = ((props.data && props.data.series) || []).filter(p => !p.date || p.date <= today)
  const dates = series.map(p => fmtAxisDate(p.date, false))
  const sel = props.metrics.filter(m => selected.value.includes(m.key))
  const hasCount = sel.some(m => m.axis === 'count')
  const hasMoney = sel.some(m => m.axis === 'money')
  const moneyAxisFmt = v => '$' + (Number(v) || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        if (!params || !params.length) return ''
        const raw = series[params[0].dataIndex]
        let html = `<div style="font-weight:600;margin-bottom:4px">${fmtAxisDate(raw && raw.date, true)}</div>`
        params.forEach(pp => {
          const key = sel[pp.seriesIndex] ? sel[pp.seriesIndex].key : null
          const val = key ? fmtVal(key, pp.value) : pp.value
          html += `<div style="display:flex;align-items:center;gap:6px">
            <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${pp.color}"></span>
            <span style="flex:1">${pp.seriesName}</span>
            <b style="margin-left:10px">${val}</b>
          </div>`
        })
        return html
      },
    },
    legend: { show: false },
    grid: { left: 8, right: 8, top: 18, bottom: 28, containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: dates, axisTick: { show: false } },
    yAxis: [
      { type: 'value', show: hasCount, splitNumber: 4 },
      { type: 'value', show: hasMoney, position: 'right', splitNumber: 4, splitLine: { show: false }, axisLabel: { formatter: moneyAxisFmt } },
    ],
    series: sel.map((m, idx) => ({
      id: m.key,
      name: m.label,
      type: 'line',
      yAxisIndex: m.axis === 'count' ? 0 : 1,
      data: series.map(p => p[m.key]),
      smooth: false,
      showSymbol: false,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: {
        width: 2.2,
        color: m.color,
        type: sel.findIndex(x => x.color === m.color) === idx ? 'solid' : 'dashed',
      },
      itemStyle: { color: m.color },
      areaStyle: sel.length === 1
        ? { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: m.color + '40' }, { offset: 1, color: m.color + '05' }] } }
        : undefined,
      emphasis: { focus: 'series' },
    })),
  }
})
</script>

<style scoped>
.msb-status-row { display: flex; justify-content: flex-end; margin-bottom: 6px; }
.msb-status { font-size: 11px; font-weight: 600; color: var(--text-muted, #64748b); padding: 3px 9px; border-radius: 999px; background: var(--kpi-bg, #f1f5f9); border: 1px solid var(--border, #e5e7eb); }
.msb-status.warn { color: #f59e0b; border-color: rgba(245, 158, 11, .35); }

.msb-kpis {
  display: grid;
  grid-template-columns: repeat(var(--kpi-cols, 6), minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
@media (max-width: 1100px) { .msb-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 560px)  { .msb-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

.msb-kpi {
  position: relative;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid var(--border, #e5e7eb);
  background: var(--kpi-bg, #f8fafc);
  border-radius: 14px;
  padding: 12px 12px 10px;
  overflow: hidden;
  transition: transform .16s ease, border-color .16s ease, box-shadow .16s ease, background .16s ease;
}
.msb-kpi:hover { transform: translateY(-2px); border-color: var(--kpi-color); }
.msb-kpi-top { position: absolute; left: 0; right: 0; top: 0; height: 3px; background: var(--kpi-color); opacity: .35; transition: opacity .16s ease, height .16s ease; }
.msb-kpi.active { border-color: var(--kpi-color); background: color-mix(in srgb, var(--kpi-color) 12%, var(--kpi-bg, #f8fafc)); box-shadow: 0 6px 18px -8px var(--kpi-color); }
.msb-kpi.active .msb-kpi-top { opacity: 1; height: 4px; }
.msb-kpi-label { display: block; font-size: 11px; font-weight: 600; letter-spacing: .3px; color: var(--text-muted, #64748b); text-transform: uppercase; }
.msb-kpi-value { display: block; margin-top: 5px; font-size: 19px; font-weight: 800; line-height: 1.1; color: var(--text, #0f172a); font-variant-numeric: tabular-nums; }
.msb-kpi-plot { display: block; margin-top: 6px; font-size: 10px; font-weight: 600; color: var(--text-dim, #94a3b8); }
.msb-kpi.active .msb-kpi-plot { color: var(--kpi-color); }

.msb-chart-wrap { min-height: 300px; display: flex; }
.msb-chart-wrap > * { width: 100%; }
.msb-empty {
  width: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text-dim, #94a3b8);
  font-size: 13px;
  border: 1px dashed var(--border-strong, #d1d9e6);
  border-radius: 12px;
  background: var(--kpi-bg, #fbfdff);
  padding: 16px;
}
</style>
